import { useEffect, useState } from "react";
import { Box, Container, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { YouTubeChannel } from "./youtubechannel.types";
import ChannelBasicInfoSection from "./components/ChannelBasicInfo";
import ReauthenticateButton from "./components/ReauthenticateButton";
import ChannelOAuthSection from "./components/ChannelOAuthSection";
import ChannelScopesSection from "./components/ChannelScopeSection";
import { fetchYouTubeChannel } from "./services/youtubeChannelService";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

export default function YouTubeChannelViewPage() {
  const { id } = useParams();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.CONFIGURATION_CHANGE);
  const [channel, setChannel] = useState<YouTubeChannel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadChannel = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const response = await fetchYouTubeChannel(Number(id));
        setChannel(response.data);
      } catch (err: unknown) {
        console.error("Failed to load YouTube channel", err);
        setError(
          (err as { message?: string })?.message ||
            "Unable to load YouTube channel details.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadChannel();
  }, [id]);

  const handleReauthenticate = () => {
    if (!channel?.reauth_url) return;
    window.location.href = channel.reauth_url;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Loading YouTube channel
        </Typography>
        <Typography color="text.secondary">
          Please wait while the channel data loads.
        </Typography>
      </Container>
    );
  }

  if (error || !channel) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          YouTube Channel not found
        </Typography>
        <Typography color="text.secondary">
          {error || "The requested YouTube channel does not exist."}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            View YouTube Channel
          </Typography>
          <Typography color="text.secondary">
            Review channel information and OAuth setup
          </Typography>
        </Box>

        {canEdit && (
          <ReauthenticateButton
            needsReauth={channel.needs_reauth}
            onClick={handleReauthenticate}
          />
        )}
      </Stack>

      <Stack spacing={3}>
        <ChannelBasicInfoSection channel={channel} />
        <ChannelOAuthSection channel={channel} />
        <ChannelScopesSection scopes={channel.scopes} />
      </Stack>
    </Container>
  );
}
